// External Dependencies
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useCallback } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';

// Internal Dependencies
import {
  AddButton,
  CopyToClipboardButton,
  PersonListItem,
  ShowPageDataDisplay,
} from 'components/shared';
import { isMobileScreenSize } from 'state/selectors';

// Local Dependencies
import DistrictOrgList from './DistrictOrgList';
import TaxExemptFormListItem from './TaxExemptFormListItem';

// Local Typings
interface Props {
  district: GQL.IDistrict;
}

// Local Variables
const StyledFirstSection = styled.section(({ theme }) => ({
  paddingBottom: theme.spacing(2),
}));

const StyledSection = styled.section(({ theme }) => ({
  padding: theme.spacing(2, 0),
}));

const StyledEmptyState = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.grey['50'],
  border: `1px solid ${theme.palette.grey['200']}`,
  borderRadius: theme.shape.borderRadius,
  padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
}));

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

// Component Definition
const DistrictInfoContent = ({ district }: Props): JSX.Element => {
  const navigate = useNavigate();

  const isMobileScreen = useSelector(isMobileScreenSize);

  const {
    admin,
    districtAssistants,
    organizations,
  } = district;

  const handleNavigateToImportOrganizations = useCallback(() => {
    navigate(`/admin_portals/${district.id}/import-organizations`);
  }, [district.id, navigate]);

  return (
    <>
      <StyledFirstSection>
        <StyledSubtitle variant="subtitle2">Presto Info</StyledSubtitle>

        <Box marginLeft={2}>
          <ShowPageDataDisplay
            label="Admin Portal ID"
            value={(
              <>
                {district.id}
                <Box
                  component="span"
                  sx={{ marginLeft: 1 }}
                >
                  <CopyToClipboardButton
                    textToCopy={district.id ?? ''}
                    tooltipTitle="Copy Admin Portal ID"
                  />
                </Box>
              </>
            )}
          />
          <ShowPageDataDisplay
            label="Number of Organizations"
            value={organizations.length}
          />
          <ShowPageDataDisplay
            label="Verified"
            type="boolean"
            value={district.isVerified}
          />
          <TaxExemptFormListItem
            district={district}
            isMobileScreen={isMobileScreen}
          />
        </Box>
      </StyledFirstSection>

      <Divider />

      <StyledSection>
        <StyledSubtitle variant="subtitle2">Admin Info</StyledSubtitle>

        <List dense={isMobileScreen}>
          <PersonListItem
            adminPortalId={district.id}
            userData={admin}
            variant="district_administrator"
          />
          {districtAssistants.map((districtAssistant) => (districtAssistant.id !== admin.id ? (
            <PersonListItem
              adminPortalId={district.id}
              key={districtAssistant.id}
              userData={districtAssistant}
              variant="district_assistant"
            />
          ) : null))}
        </List>
      </StyledSection>

      <Divider />

      <Box
        component="section"
        sx={{
          paddingTop: 2,
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <StyledSubtitle variant="subtitle2">Organizations</StyledSubtitle>

          <AddButton
            label="Organizations"
            onClick={handleNavigateToImportOrganizations}
          />
        </Box>

        {organizations.length > 0 ? (
          <DistrictOrgList organizations={organizations} />
        ) : (
          <StyledEmptyState color="textSecondary">
            No organizations yet.
          </StyledEmptyState>
        )}
      </Box>
    </>
  );
};

export default DistrictInfoContent;
